import css from './index.module.scss';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Button, Typography } from '@components/base';
import { BoxContainer, InputDatePicker, TextField } from '@components/common';
import IconNode from '@components/base/icon-node';
import Images from '@assets/images';

interface PackagingFormProps {
  onFormSubmit: (data: any) => void;
  onCancelClick: () => void;
  rectifyRemarks?: string;
}

const BatchForm = (props: PackagingFormProps) => {
  const { onFormSubmit, onCancelClick } = props;
  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useFormContext<any>();

  const { fields: itemFields } = useFieldArray({
    name: `items`,
    control
  });

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className={css.formWrapper}>
      <BoxContainer title="Item details">
        <ItemWrapper itemFields={itemFields} control={control} />
      </BoxContainer>
      <div className={css.actionBtnWrapper}>
        <Button variant="text" onClick={onCancelClick}>
          Cancel
        </Button>
        <div>
          <Button type="submit" disabled={isSubmitting}>
            Save and Proceed
          </Button>
        </div>
      </div>
    </form>
  );
};

interface ItemWrapperProps {
  itemFields: any;
  control: any;
}

const ItemWrapper = (props: ItemWrapperProps) => {
  const { itemFields, control } = props;
  return (
    <>
      {itemFields.map((item: any, index: number) => {
        return (
          <div className={css.itemWrapper} key={item.id}>
            <Controller
              name={`items.${index}.name`}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  label="Item Name"
                  placeholder="Item Name"
                  className={css.fieldWrapper}
                  {...field}
                  disabled
                  error={fieldState.invalid}
                  endIcon={
                    fieldState.invalid && <IconNode src={Images.alertError} alt="Error Icon" />
                  }
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <BatchWrapper index={index} item={item} />
          </div>
        );
      })}
    </>
  );
};

interface PackageWrapperProps {
  index: number;
  item: any;
}

const BatchWrapper = (props: PackageWrapperProps) => {
  const { index, item } = props;
  const { control } = useFormContext<any>();

  const {
    fields: batchFields,
    append: batchAppend,
    remove: batchRemove
  } = useFieldArray({
    name: `items.${index}.batch`,
    control
  });

  const handleAddBatchField = () => {
    batchAppend({
      batch_number: null
    });
  };

  return (
    <div className={css.pacakageSectionWrapper}>
      <Typography variant="p" className={css.titleWrapper}>
        Batch Number
      </Typography>
      <div className={css.boxWrapper}>
        {batchFields.map((batch: any, batchIndex: number) => {
          const handleDeleteClick = () => batchRemove(batchIndex);
          return (
            <div key={batch.id} className={css.rowWrapper}>
              <Controller
                name={`items.${index}.batch.${batchIndex}.batch_number`}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Batch Number"
                    required
                    placeholder="Batch Number"
                    className={css.fieldWrapper}
                    {...field}
                    error={fieldState.invalid}
                    endIcon={
                      fieldState.invalid && <IconNode src={Images.alertError} alt="Error Icon" />
                    }
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name={`items.${index}.batch.${batchIndex}.manufacturing_date`}
                control={control}
                render={({ field, fieldState }) => (
                  <InputDatePicker
                    {...field}
                    value={field.value}
                    required
                    placeholder="Select date"
                    label="Manufacturing Date"
                    onSelect={(day: Date | undefined) => field.onChange(day ?? null)}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    rootClassName={css.fieldSpacing}
                  />
                )}
              />
              <Controller
                name={`items.${index}.batch.${batchIndex}.expiry_date`}
                control={control}
                render={({ field, fieldState }) => (
                  <InputDatePicker
                    {...field}
                    value={field.value}
                    required
                    placeholder="Select date"
                    label="Expiry Date"
                    onSelect={(day: Date | undefined) => field.onChange(day ?? null)}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    rootClassName={css.fieldSpacing}
                  />
                )}
              />
              {batchFields.length > 1 && (
                <Button
                  variant="text"
                  title="Delete Item"
                  onClick={handleDeleteClick}
                  startIcon={<IconNode src={Images.deleteRed} alt="delete icon" />}></Button>
              )}
            </div>
          );
        })}
        <Button
          variant="text"
          onClick={handleAddBatchField}
          startIcon={<IconNode src={Images.plusRed} alt="add icon" />}>
          Add Batch Number
        </Button>
      </div>
    </div>
  );
};

export default BatchForm;
